// @ts-nocheck
import { Stack, Title, Text as MantineText, Code, SimpleGrid } from '@mantine/core';
import { Card } from '../../../components/ui';
import { ScrollReveal, Parallax, StaggeredList } from '../../../components/animations';

export default function ScrollPage() {
  const listItems = [
    'First item - appears with delay',
    'Second item - appears with more delay',
    'Third item - appears with even more delay',
    'Fourth item - staggered animation',
    'Fifth item - smooth sequential reveal',
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Scroll Animations</Title>
        <MantineText c="dimmed" size="sm">
          Animations triggered by scrolling - reveal elements as they enter the viewport
        </MantineText>
      </Stack>

      {/* ScrollReveal */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ScrollReveal</Title>
            <MantineText size="sm" c="dimmed">
              Reveal elements as they scroll into view
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <MantineText size="xs" c="dimmed" mb="md">
              Scroll down to see cards reveal
            </MantineText>

            <Stack gap="md">
              <ScrollReveal>
                <Card variant="subtle" p="md">
                  <MantineText fw={600}>Card 1</MantineText>
                  <MantineText size="sm" c="dimmed">
                    This card reveals when scrolled into view
                  </MantineText>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={200}>
                <Card variant="subtle" p="md">
                  <MantineText fw={600}>Card 2</MantineText>
                  <MantineText size="sm" c="dimmed">
                    This card has a 200ms delay
                  </MantineText>
                </Card>
              </ScrollReveal>

              <ScrollReveal delay={400}>
                <Card variant="subtle" p="md">
                  <MantineText fw={600}>Card 3</MantineText>
                  <MantineText size="sm" c="dimmed">
                    This card has a 400ms delay
                  </MantineText>
                </Card>
              </ScrollReveal>
            </Stack>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<ScrollReveal>
  <Card variant="subtle">
    <Text fw={600}>Card 1</Text>
    <Text size="sm" c="dimmed">
      This card reveals when scrolled into view
    </Text>
  </Card>
</ScrollReveal>

<ScrollReveal delay={200}>
  <Card variant="subtle">
    <Text fw={600}>Card 2</Text>
    <Text size="sm" c="dimmed">
      This card has a 200ms delay
    </Text>
  </Card>
</ScrollReveal>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • animation - 'fade' | 'slide-up' | 'slide-down' | 'scale' (default: 'fade')
              <br />
              • duration - Animation duration in ms (default: 600)
              <br />
              • delay - Delay after entering viewport (default: 0)
              <br />
              • threshold - Visibility threshold 0-1 (default: 0.1)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Parallax */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Parallax</Title>
            <MantineText size="sm" c="dimmed">
              Create depth with parallax scrolling effect
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <div style={{ height: 300, overflow: 'hidden', position: 'relative' }}>
              <Parallax speed={0.5}>
                <Card
                  variant="gradient"
                  gradientVariant="primary"
                  p="xl"
                  style={{ height: 200 }}
                >
                  <Stack gap="xs" justify="center" h="100%">
                    <MantineText c="white" fw={700} size="xl">
                      Parallax Background
                    </MantineText>
                    <MantineText c="white" opacity={0.9}>
                      Scrolls at 50% speed
                    </MantineText>
                  </Stack>
                </Card>
              </Parallax>
            </div>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Parallax speed={0.5}>
  <Card variant="gradient" gradientVariant="primary">
    <Text c="white" fw={700} size="xl">
      Parallax Background
    </Text>
    <Text c="white" opacity={0.9}>
      Scrolls at 50% speed
    </Text>
  </Card>
</Parallax>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • speed - Parallax speed multiplier (default: 0.5)
              <br />
              • direction - 'vertical' | 'horizontal' (default: 'vertical')
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* StaggeredList */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>StaggeredList</Title>
            <MantineText size="sm" c="dimmed">
              Animate list items with sequential delays
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example:</MantineText>
            <StaggeredList stagger={100}>
              {listItems.map((item, index) => (
                <Card key={index} variant="outline" p="sm">
                  <MantineText size="sm">{item}</MantineText>
                </Card>
              ))}
            </StaggeredList>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`const items = [
  'First item',
  'Second item',
  'Third item',
  'Fourth item',
  'Fifth item',
];

<StaggeredList stagger={100}>
  {items.map((item, index) => (
    <Card key={index} variant="outline">
      <Text size="sm">{item}</Text>
    </Card>
  ))}
</StaggeredList>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • stagger - Delay between items in ms (default: 100)
              <br />
              • animation - 'fade' | 'slide' | 'scale' (default: 'fade')
              <br />
              • duration - Animation duration in ms (default: 300)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Landing Pages</MantineText>
              <MantineText size="xs" c="dimmed">
                Use ScrollReveal for hero sections, feature blocks, and testimonials to create engaging scroll experiences.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Long-Form Content</MantineText>
              <MantineText size="xs" c="dimmed">
                Apply Parallax to background images and sections for depth and visual interest in articles or portfolios.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Product Galleries</MantineText>
              <MantineText size="xs" c="dimmed">
                Use StaggeredList for product grids, pricing tables, or feature comparisons with sequential reveals.
              </MantineText>
            </Stack>

            <Stack gap="xs">
              <MantineText size="sm" fw={500}>Data Dashboards</MantineText>
              <MantineText size="xs" c="dimmed">
                Apply ScrollReveal to charts, cards, and stats as users scroll through dashboard sections.
              </MantineText>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
